import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useNavigate } from "react-router-dom"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Row, Col, CardHeader, CardBody, Spinner, Alert,
  FormGroup, Label, Input, FormFeedback, Button,
} from "reactstrap"
import { toast } from "react-toastify"
import {
  fetchPlans,
  fetchInvoiceInfo,
  calculatePrice,
  validateCouponAction,
  clearCoupon,
} from "../../store/subscription/actions"
import { getUpgradePreview, initiate3DSPayment, getSubscriptionInstallmentGroup } from "../../api/requests/subscription-requests"
import { fetchBinDetail } from "../../api/requests/payment-requests"
import { fetchAuthMe } from "../../api/requests/auth-requests"
import { fetchCompanySettings } from "../../api/requests/company-requests"
import { getReferralOffer } from "../../api/requests/referral-requests"
import { API } from "../../api/urls"
import BackButton from "../../components/Common/BackButton";
import { resolvePlanPricing } from "../../utils/planPricing"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"
const ORANGE = "#E56A22"

const PLAN_COLORS = [
  { accent: "#221C5D", light: "#EBF0F7" },
  { accent: "#E56A22", light: "#FEF5EB" },
  { accent: "#71C30B", light: "#F0FAE5" },
  { accent: "#7FCDE3", light: "#EAF7FB" },
  { accent: "#E07A7A", light: "#FDEEEE" },
  { accent: "#9B59B6", light: "#F4ECF7" },
]

const Checkout = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { planId } = useParams()
  const upgradeType = new URLSearchParams(window.location.search).get("upgradeType")
  const [couponCode, setCouponCode] = useState("")
  const [companyInfo, setCompanyInfo] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [upgradePreview, setUpgradePreview] = useState(null)
  const [referralOffer, setReferralOffer] = useState(null)

  useEffect(() => {
    getReferralOffer()
      .then((res) => setReferralOffer(res?.data?.eligible ? res.data : null))
      .catch(() => setReferralOffer(null))
  }, [])

  // Paket taksidi: oranlar ödeyen firmadan değil, platformun işaretli taksit
  // grubundan gelir. Grup işaretli değilse uç 404 döner ve taksit hiç gösterilmez.
  const [installmentOptions, setInstallmentOptions] = useState([])
  const [cardBrand, setCardBrand] = useState(null)
  // BIN sorgusu firma kimliği ister. companyInfo, company-settings yanıtı olduğu
  // için id taşıdığı garanti değil; kimliği auth/me'den doğrudan saklıyoruz.
  const [payerCompanyId, setPayerCompanyId] = useState(null)
  const [installmentCount, setInstallmentCount] = useState(1)

  const {
    plans,
    invoiceInfo,
    priceCalculation,
    couponValidation,
    loading,
  } = useSelector((state) => state.subscription)

  const selectedPlan = plans.find((p) => String(p.id) === String(planId))
  const planIndex = plans.findIndex((p) => String(p.id) === String(planId))
  const planColor = PLAN_COLORS[planIndex >= 0 ? planIndex % PLAN_COLORS.length : 0]
  const isRenewal = selectedPlan?.purchaseType === "RENEWAL"
  const isDowngrade = selectedPlan?.purchaseType === "DOWNGRADE"
  // Yükseltmede tutarı backend önizlemesi belirler: kalan süre ya da kalan değeri düşülmüş yeni dönem.
  const { payable: listPayable, struck: listPrice } = resolvePlanPricing(priceCalculation)
  const listDiscount = listPrice != null ? listPrice - listPayable : 0
  const upgradeOption = upgradeType === "IMMEDIATE" ? upgradePreview?.immediateOption
    : upgradeType === "EXTENSION" ? upgradePreview?.extensionOption
    : null

  // Taksit seçenekleri her paket akışında (alım, yenileme, yükseltme) aynı yerden gelir.
  useEffect(() => {
    let cancelled = false
    getSubscriptionInstallmentGroup()
      .then((json) => {
        if (cancelled) return
        // 404 = işaretli grup yok; hata değil, taksit kapalı demek.
        setInstallmentOptions(json?.statusCode === 200 ? (json.data?.options || []) : [])
      })
      .catch(() => { if (!cancelled) setInstallmentOptions([]) })
    return () => { cancelled = true }
  }, [])

  /** Vade farkı backend'le aynı formülle: tutar / ((100 - oran) / 100) */
  const totalWithInstallment = (base, commissionPercent) => {
    const divisor = (100 - Number(commissionPercent)) / 100
    return divisor > 0 ? Number(base) / divisor : Number(base)
  }

  const styles = `
    .checkout-modern .btn-rounded { border-radius: 999px; }
    .checkout-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .checkout-modern .btn-navy:hover { filter: brightness(1.08); }
    .checkout-modern .btn-navy:disabled { opacity: .65; }
    .checkout-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
    .checkout-modern .summary-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: .55rem 0; font-size: .9rem;
    }
    .checkout-modern .summary-divider { border-top: 1px dashed #dee2e6; margin: .25rem 0; }
    .checkout-modern .section-card {
      border-radius: 1rem; background: #fff; padding: 1.5rem; margin-bottom: 1rem;
      border: 1px solid #e9ecef; transition: box-shadow .2s;
    }
    .checkout-modern .section-card:hover { box-shadow: 0 .25rem .75rem rgba(0,0,0,.06); }
    .checkout-modern .section-title {
      font-weight: 700; font-size: 1rem; color: #333;
      margin-bottom: 1rem; display: flex; align-items: center; gap: .5rem;
    }
    .checkout-modern .section-icon {
      width: 36px; height: 36px; border-radius: .75rem;
      display: flex; align-items: center; justify-content: center;
      font-size: 18px; flex-shrink: 0;
    }
    .checkout-modern .coupon-badge {
      display: inline-flex; align-items: center; gap: .35rem;
      font-size: .8rem; padding: .35rem .75rem; border-radius: 999px; font-weight: 500;
    }
    .checkout-modern .plan-hero {
      border-radius: 1rem; padding: 1.25rem; text-align: center; margin-bottom: 1rem;
    }
    .checkout-modern .total-box {
      border-radius: .75rem; padding: 1rem; text-align: center;
    }
    .checkout-modern .card-input { font-family: 'Courier New', monospace; letter-spacing: 1px; }
  `

  document.title = "Ödeme | Tahsilat360"

  useEffect(() => {
    // Koşulsuz tazelenir: süper admin paketi düzenlemiş olabilir ve ödeme ekranı
    // eski fiyat/özellik listesini göstermemeli. (Önceden yalnız liste boşken
    // çekiliyordu, yani store'daki eski kopya olduğu gibi kullanılıyordu.)
    dispatch(fetchPlans())
    dispatch(fetchInvoiceInfo())

    const loadCompanyInfo = async () => {
      try {
        const meRes = await fetchAuthMe()
        const meJson = await meRes.json()
        const companyId = meJson.data?.companyId
        if (!companyId) return
        setPayerCompanyId(companyId)
        const settingsJson = await fetchCompanySettings(companyId)
        if (settingsJson.data) setCompanyInfo(settingsJson.data)
      } catch {}
    }
    loadCompanyInfo()
  }, [dispatch, plans.length])

  useEffect(() => {
    if (!selectedPlan) return
    if (upgradeType) {
      getUpgradePreview(selectedPlan.id)
        .then(res => { if (res.data) setUpgradePreview(res.data) })
        .catch(() => {})
    }
    dispatch(calculatePrice({ planId: selectedPlan.id }))
  }, [dispatch, selectedPlan])

  useEffect(() => {
    if (couponValidation?.valid && selectedPlan) {
      if (upgradeType) {
        getUpgradePreview(selectedPlan.id, couponCode)
          .then(res => { if (res.data) setUpgradePreview(res.data) })
          .catch(() => {})
      }
      dispatch(calculatePrice({ planId: selectedPlan.id, couponCode }))
    }
  }, [couponValidation])

  const inv = invoiceInfo || {}
  const comp = companyInfo || {}

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Fatura — backend InvoiceInfoDTO field'ları: invoice* prefix'li
      companyName: inv.invoiceCompanyName || inv.companyName || comp.companyName || "",
      // VKN ve TC aynı alanda gösteriliyor — şahıs firması TC, diğerleri VKN
      taxNumber: inv.invoiceTaxNumber || inv.invoiceTcNumber || inv.taxNumber || comp.invoiceTaxNumber || comp.invoiceTcNumber || "",
      taxOffice: inv.invoiceTaxOffice || inv.taxOffice || comp.invoiceTaxOffice || "",
      address: inv.invoiceAddress || inv.address || comp.address || "",
      // Kart
      cc_owner: "",
      card_number: "",
      expiry_month: "",
      expiry_year: "",
      cvv: "",
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required("Firma adı gerekli"),
      taxNumber: Yup.string().required("Vergi no / TC no gerekli"),
      taxOffice: Yup.string().required("Vergi dairesi gerekli"),
      address: Yup.string().required("Adres gerekli"),
      cc_owner: Yup.string().required("Kart üzerindeki isim gerekli"),
      card_number: Yup.string()
        .test("card-len", "Geçerli bir kart numarası girin", (v) => {
          const digits = (v || "").replace(/\s/g, "")
          return digits.length >= 15 && digits.length <= 16
        })
        .required("Kart numarası gerekli"),
      expiry_month: Yup.string().required("Ay seçin"),
      expiry_year: Yup.string().required("Yıl seçin"),
      cvv: Yup.string()
        .matches(/^\d{3,4}$/, "Geçerli CVV girin")
        .required("CVV gerekli"),
    }),
    onSubmit: async (values) => {
      if (!selectedPlan) return
      setSubmitting(true)
      setErrorMessage("")

      const basePayload = {
        planId: selectedPlan.id,
        couponCode: couponValidation?.valid ? couponCode : null,
        invoiceCompanyName: values.companyName,
        invoiceTaxNumber: values.taxNumber,
        invoiceTaxOffice: values.taxOffice,
        invoiceAddress: values.address,
        ccOwner: values.cc_owner,
        cardNumber: (values.card_number || "").replace(/\s/g, ""),
        expiryMonth: values.expiry_month,
        expiryYear: values.expiry_year,
        cvv: values.cvv,
        installmentCount,
      }

      const payload = upgradeType
        ? { ...basePayload, upgradeType, targetPlanId: selectedPlan.id }
        : basePayload

      try {
        const resp = await initiate3DSPayment(payload, !!upgradeType)

        const cType = resp.headers.get("content-type") || ""
        let htmlToRender = null

        if (cType.includes("application/json")) {
          const json = await resp.json()
          if (resp.ok && json.statusCode === 200) {
            htmlToRender = json.data
          } else {
            if (resp.status === 409) {
              setErrorMessage(json.message || "Zaten bekleyen bir ödeme işleminiz bulunmaktadır. Lütfen daha sonra tekrar deneyin.")
            } else {
              setErrorMessage(json.message || "Ödeme başlatılamadı")
            }
            setSubmitting(false)
            return
          }
        } else {
          htmlToRender = await resp.text()
        }

        // 3D Secure formu render et
        if (htmlToRender) {
          document.open("text/html", "replace")
          document.write(htmlToRender)
          document.close()
        }
      } catch (err) {
        setErrorMessage(err.message || "Beklenmeyen bir hata oluştu")
        setSubmitting(false)
      }
    },
  })

  // Taksit oranı kart markasına bağlı; marka BIN'den çözülür. Marka bilinmeden
  // oran gösterirsek kullanıcıya yanlış tutar göstermiş oluruz.
  const cardDigits = (formik?.values?.card_number || "").replace(/\D/g, "")
  useEffect(() => {
    if (cardDigits.length < 6 || !payerCompanyId) {
      setCardBrand(null)
      return
    }
    const ctrl = new AbortController()
    fetchBinDetail(cardDigits.slice(0, 6), payerCompanyId, ctrl.signal)
      .then((res) => res.json())
      .then((json) => setCardBrand(json?.statusCode === 200 ? (json.data?.brand || null) : null))
      .catch(() => setCardBrand(null))
    return () => ctrl.abort()
  }, [cardDigits.slice(0, 6), payerCompanyId])

  // Kartın markasına ait, açık taksitler. Marka bilinmiyorsa liste boş kalır ve
  // ekran tek çekimde durur.
  const availableInstallments = React.useMemo(() => {
    if (!cardBrand) return []
    return installmentOptions
      .filter((o) => String(o.cardBrand).toUpperCase() === String(cardBrand).toUpperCase())
      .filter((o) => Number(o.installmentCount) > 1)
      .sort((a, b) => a.installmentCount - b.installmentCount)
  }, [installmentOptions, cardBrand])

  // Seçili taksit kart değişince geçersiz kalabilir; tek çekime düşülür.
  useEffect(() => {
    if (installmentCount > 1 && !availableInstallments.some((o) => o.installmentCount === installmentCount)) {
      setInstallmentCount(1)
    }
  }, [availableInstallments, installmentCount])


  const handleValidateCoupon = () => {
    if (!couponCode.trim()) return
    dispatch(validateCouponAction({ code: couponCode, planId }))
  }

  const handleClearCoupon = () => {
    setCouponCode("")
    dispatch(clearCoupon())
    if (selectedPlan) {
      dispatch(calculatePrice({ planId: selectedPlan.id }))
      if (upgradeType) {
        getUpgradePreview(selectedPlan.id)
          .then(res => { if (res.data) setUpgradePreview(res.data) })
          .catch(() => {})
      }
    }
  }

  const formatPrice = (price) => {
    if (price == null) return "-"
    return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"
  }

  // Kart numarasını boşluklu formatla (payment formu ile aynı)
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "")
    const match = v.match(/\d{4,16}/g)?.[0] || v
    const parts = []
    for (let i = 0; i < match.length; i += 4) parts.push(match.substring(i, i + 4))
    return parts.length ? parts.join(" ") : v
  }

  const handleCardNumberChange = (e) => {
    formik.setFieldValue("card_number", formatCardNumber(e.target.value))
  }

  const handleOwnerChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-ZğüşöçıİĞÜŞÖÇ\s]/g, "")
    formik.setFieldValue("cc_owner", value)
  }

  // Yıl seçenekleri
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 15 }, (_, i) => currentYear + i)

  if (!selectedPlan && plans.length > 0) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="warning" className="rounded-4">
            <i className="bx bx-error-circle me-2" />
            Seçilen paket bulunamadı.
            <button className="btn btn-sm btn-outline-warning ms-2 btn-rounded" onClick={() => navigate("/subscription/plans")}>
              Paketlere Dön
            </button>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content checkout-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row>
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <div className="d-flex align-items-center gap-3">
                  <BackButton to={upgradeType ? `/subscription/upgrade/${planId}` : "/subscription/plans"} />
                  <div>
                    <h5 className="mb-0 fw-bold text-dark">Ödeme</h5>
                    {selectedPlan && (
                      <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                        {selectedPlan.name} — {isRenewal ? "Abonelik Yenileme" : `${selectedPlan.durationLabel || ""} Abonelik`.trim()}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardBody className="pt-3">
                {(errorMessage || formik.submitCount > 0 && Object.keys(formik.errors).length > 0) && (
                  <Alert color="danger" className="mb-3 rounded-4">
                    <i className="bx bx-error-circle me-2" />
                    {errorMessage || "Lütfen tüm alanları doğru şekilde doldurun."}
                  </Alert>
                )}

                <Row>
                  {/* Sol — Formlar */}
                  <Col lg={7}>
                    {/* Fatura Bilgileri */}
                    <div className="section-card">
                      <div className="section-title">
                        <div className="section-icon" style={{ backgroundColor: "#EBF0F7", color: NAVY }}>
                          <i className="bx bx-receipt" />
                        </div>
                        Fatura Bilgileri
                      </div>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label className="small fw-medium text-muted">Firma Adı</Label>
                            <Input name="companyName" value={formik.values.companyName} onChange={formik.handleChange} onBlur={formik.handleBlur}
                              invalid={formik.touched.companyName && !!formik.errors.companyName} />
                            <FormFeedback>{formik.errors.companyName}</FormFeedback>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label className="small fw-medium text-muted">Vergi No / TC No</Label>
                            <Input name="taxNumber" value={formik.values.taxNumber} onChange={formik.handleChange} onBlur={formik.handleBlur}
                              invalid={formik.touched.taxNumber && !!formik.errors.taxNumber} />
                            <FormFeedback>{formik.errors.taxNumber}</FormFeedback>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label className="small fw-medium text-muted">Vergi Dairesi</Label>
                            <Input name="taxOffice" value={formik.values.taxOffice} onChange={formik.handleChange} onBlur={formik.handleBlur}
                              invalid={formik.touched.taxOffice && !!formik.errors.taxOffice} />
                            <FormFeedback>{formik.errors.taxOffice}</FormFeedback>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="small fw-medium text-muted">Adres</Label>
                            <Input type="textarea" name="address" rows={2} value={formik.values.address} onChange={formik.handleChange} onBlur={formik.handleBlur}
                              invalid={formik.touched.address && !!formik.errors.address} />
                            <FormFeedback>{formik.errors.address}</FormFeedback>
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>

                    {/* Kart Bilgileri */}
                    <div className="section-card">
                      <div className="section-title">
                        <div className="section-icon" style={{ backgroundColor: "#F0FAE5", color: "#71C30B" }}>
                          <i className="bx bx-credit-card" />
                        </div>
                        Kart Bilgileri
                      </div>
                      <FormGroup className="mb-3">
                        <Input
                          type="text"
                          placeholder="* Kart Üzerindeki İsim"
                          value={formik.values.cc_owner}
                          onChange={handleOwnerChange}
                          onBlur={formik.handleBlur}
                          name="cc_owner"
                          invalid={formik.touched.cc_owner && !!formik.errors.cc_owner}
                        />
                        <FormFeedback>{formik.errors.cc_owner}</FormFeedback>
                      </FormGroup>
                      <FormGroup className="mb-3">
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9 ]*"
                          placeholder="* Kart Numarası (Örn: 4355 0843 5508 4358)"
                          maxLength={19}
                          value={formik.values.card_number}
                          onChange={handleCardNumberChange}
                          onBlur={formik.handleBlur}
                          name="card_number"
                          invalid={formik.touched.card_number && !!formik.errors.card_number}
                        />
                        <FormFeedback>{formik.errors.card_number}</FormFeedback>
                      </FormGroup>
                      <Row>
                        <Col xs={4}>
                          <FormGroup className="mb-3">
                            <Input
                              type="select"
                              name="expiry_month"
                              value={formik.values.expiry_month}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={formik.touched.expiry_month && !!formik.errors.expiry_month}
                            >
                              <option value="">Ay</option>
                              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                <option key={m} value={m.toString().padStart(2, "0")}>
                                  {m.toString().padStart(2, "0")}
                                </option>
                              ))}
                            </Input>
                            <FormFeedback>{formik.errors.expiry_month}</FormFeedback>
                          </FormGroup>
                        </Col>
                        <Col xs={4}>
                          <FormGroup className="mb-3">
                            <Input
                              type="select"
                              name="expiry_year"
                              value={formik.values.expiry_year}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={formik.touched.expiry_year && !!formik.errors.expiry_year}
                            >
                              <option value="">Yıl</option>
                              {yearOptions.map((y) => (
                                <option key={y} value={y.toString().slice(-2)}>
                                  {y.toString().slice(-2)}
                                </option>
                              ))}
                            </Input>
                            <FormFeedback>{formik.errors.expiry_year}</FormFeedback>
                          </FormGroup>
                        </Col>
                        <Col xs={4}>
                          <FormGroup className="mb-3">
                            <Input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="CVV"
                              maxLength={4}
                              name="cvv"
                              value={formik.values.cvv}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={formik.touched.cvv && !!formik.errors.cvv}
                            />
                            <FormFeedback>{formik.errors.cvv}</FormFeedback>
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>

                    {/* Taksit Seçenekleri — yalnız kartın markasında açık taksit varsa.
                        Marka BIN'den çözülene kadar ekran tek çekimde durur; oran
                        markaya bağlı olduğu için erken göstermek yanlış tutar demek. */}
                    {availableInstallments.length > 0 && (
                      <div className="section-card">
                        <h6 className="mb-3">Taksit Seçenekleri</h6>
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            outline={installmentCount !== 1}
                            color="primary"
                            className="btn-rounded"
                            onClick={() => setInstallmentCount(1)}
                          >
                            Tek Çekim
                          </Button>

                          {availableInstallments.map((o) => {
                            const base = upgradeOption ? upgradeOption.totalAmount : priceCalculation?.totalPrice
                            const total = base != null ? totalWithInstallment(base, o.commissionPercent) : null
                            return (
                              <Button
                                key={o.installmentCount}
                                type="button"
                                size="sm"
                                outline={installmentCount !== o.installmentCount}
                                color="primary"
                                className="btn-rounded"
                                onClick={() => setInstallmentCount(o.installmentCount)}
                              >
                                {o.installmentCount} Taksit
                                {total != null && (
                                  <span className="ms-1 small opacity-75">
                                    ({formatPrice(total)})
                                  </span>
                                )}
                              </Button>
                            )
                          })}
                        </div>
                        {installmentCount > 1 && (
                          <div className="text-muted small mt-2">
                            Taksitli ödemede vade farkı toplam tutara yansır.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Kupon Kodu */}
                    <div className="section-card">
                      <div className="section-title">
                        <div className="section-icon" style={{ backgroundColor: "#FEF5EB", color: ORANGE }}>
                          <i className="bx bx-purchase-tag" />
                        </div>
                        Kupon Kodu
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <Input placeholder="Kupon kodunuzu girin" value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          disabled={couponValidation?.valid} className="pill-input" style={{ maxWidth: 280 }} />
                        {couponValidation?.valid ? (
                          <button className="btn btn-outline-danger btn-rounded px-3 py-2" onClick={handleClearCoupon} style={{ whiteSpace: "nowrap" }}>
                            <i className="bx bx-x me-1" />Kaldır
                          </button>
                        ) : (
                          <button className="btn btn-navy btn-rounded px-3 py-2" onClick={handleValidateCoupon}
                            disabled={!couponCode.trim() || loading} style={{ whiteSpace: "nowrap" }}>
                            Uygula
                          </button>
                        )}
                      </div>
                      {couponValidation && (
                        <div className="mt-2">
                          {couponValidation.valid ? (
                            <span className="coupon-badge" style={{ backgroundColor: "#E8F5E9", color: "#2E7D32" }}>
                              <i className="bx bx-check-circle" />
                              Kupon uygulandı — {couponValidation.discountType === "PERCENTAGE"
                                ? `%${couponValidation.discountValue} indirim`
                                : `${formatPrice(couponValidation.discountValue)} indirim`}
                            </span>
                          ) : (
                            <span className="coupon-badge" style={{ backgroundColor: "#FFEBEE", color: "#C62828" }}>
                              <i className="bx bx-x-circle" />
                              {couponValidation.reason || "Geçersiz kupon kodu"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Sağ — Sipariş Özeti */}
                  <Col lg={5}>
                    <div className="section-card sticky-top" style={{ top: 90, border: `2px solid ${planColor.accent}30` }}>
                      <div className="plan-hero" style={{ backgroundColor: planColor.light }}>
                        <span className="badge mb-2" style={{ borderRadius: 999, backgroundColor: planColor.accent, color: "#fff", fontSize: ".75rem", padding: ".35rem .75rem" }}>
                          {selectedPlan?.durationLabel || "—"}
                        </span>
                        <h5 className="fw-bold mb-1" style={{ color: planColor.accent }}>{selectedPlan?.name}</h5>
                        {selectedPlan?.description && <p className="text-muted small mb-0">{selectedPlan.description}</p>}
                      </div>

                      {(isRenewal || isDowngrade) && selectedPlan?.currentPlanEndDate && (
                        <div className="d-flex align-items-start gap-2 mb-3 p-3" style={{
                          backgroundColor: "#F0FAE5", borderRadius: ".75rem", border: "1px solid #71C30B40"
                        }}>
                          <i className={`bx ${isDowngrade ? "bx-down-arrow-alt" : "bx-refresh"}`} style={{ color: "#71C30B", fontSize: "1.2rem", marginTop: 2 }} />
                          <div style={{ fontSize: ".85rem" }}>
                            <strong style={{ color: "#2E7D32" }}>{isDowngrade ? "Dönem Sonunda Paket Değişikliği" : "Abonelik Yenileme"}</strong>
                            <p className="mb-0 text-muted mt-1">
                              Yeni aboneliğiniz mevcut dönem bitişinden ({new Date(selectedPlan.currentPlanEndDate).toLocaleDateString("tr-TR")}) itibaren başlayacaktır.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="section-title" style={{ marginBottom: ".5rem" }}>
                        <div className="section-icon" style={{ backgroundColor: planColor.light, color: planColor.accent }}>
                          <i className="bx bx-calculator" />
                        </div>
                        Fiyat Detayları
                      </div>

                      {upgradeOption ? (
                        <>
                          {upgradeType === "IMMEDIATE" ? (
                            <div className="summary-row">
                              <span className="text-muted">Kalan {upgradePreview.remainingDays} günün bedeli</span>
                              <span className="fw-medium">{formatPrice(upgradeOption.targetPlanRemainingCost)}</span>
                            </div>
                          ) : (
                            <>
                              <div className="summary-row">
                                <span className="text-muted">Paket fiyatı</span>
                                <span className="fw-medium">{formatPrice(upgradeOption.basePrice)}</span>
                              </div>
                              {upgradeOption.couponDiscount > 0 && (
                                <div className="summary-row">
                                  <span className="text-muted">Kupon İndirimi</span>
                                  <span className="fw-medium" style={{ color: "#2E7D32" }}>-{formatPrice(upgradeOption.couponDiscount)}</span>
                                </div>
                              )}
                              {upgradeOption.currentPlanCredit > 0 && (
                                <div className="summary-row">
                                  <span className="text-muted">Mevcut paketin kalan değeri</span>
                                  <span className="fw-medium" style={{ color: "#2E7D32" }}>-{formatPrice(upgradeOption.currentPlanCredit)}</span>
                                </div>
                              )}
                            </>
                          )}
                          <div className="summary-divider" />
                          <div className="total-box mt-2" style={{ backgroundColor: planColor.light }}>
                            <div className="text-muted small mb-1">Ödenecek Tutar</div>
                            <div className="fw-bold" style={{ fontSize: "2rem", color: planColor.accent, lineHeight: 1.2 }}>
                              {formatPrice(upgradeOption.totalAmount)}
                            </div>
                          </div>
                          <div className="text-muted small mt-2 text-center">
                            {upgradeOption.startDate && upgradeOption.endDate && (
                              <span>{new Date(upgradeOption.startDate).toLocaleDateString("tr-TR")} - {new Date(upgradeOption.endDate).toLocaleDateString("tr-TR")}</span>
                            )}
                          </div>
                        </>
                      ) : priceCalculation ? (
                        <>
                          <div className="summary-row">
                            <span className="text-muted">Liste Fiyatı</span>
                            <span className="fw-medium">{formatPrice(priceCalculation.basePrice)}</span>
                          </div>
                          {listDiscount > 0 && (
                            <div className="summary-row">
                              <span className="text-muted">{priceCalculation.hasOverride ? "Özel Fiyat İndirimi" : "Paket İndirimi"}</span>
                              <span className="fw-medium" style={{ color: "#2E7D32" }}>
                                <i className="bx bx-minus me-1" style={{ fontSize: 12 }} />{formatPrice(listDiscount)}
                              </span>
                            </div>
                          )}
                          {priceCalculation.couponDiscount > 0 && (
                            <div className="summary-row">
                              <span className="text-muted">Kupon İndirimi</span>
                              <span className="fw-medium" style={{ color: "#2E7D32" }}>
                                <i className="bx bx-minus me-1" style={{ fontSize: 12 }} />{formatPrice(priceCalculation.couponDiscount)}
                              </span>
                            </div>
                          )}
                          <div className="summary-divider" />
                          <div className="total-box mt-2" style={{ backgroundColor: planColor.light }}>
                            <div className="text-muted small mb-1">Ödenecek Tutar</div>
                            <div className="fw-bold" style={{ fontSize: "2rem", color: planColor.accent, lineHeight: 1.2 }}>
                              {formatPrice(priceCalculation.totalPrice)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <Spinner size="sm" color="primary" />
                          <div className="text-muted small mt-2">Fiyat hesaplanıyor...</div>
                        </div>
                      )}

                      {referralOffer && (
                        <div className="alert alert-success py-2 mt-3 mb-0" style={{ borderRadius: 12, fontSize: ".9rem" }}>
                          <i className="bx bx-gift me-1" />
                          Davet hediyesi: bu paketle aboneliğinize <strong>{referralOffer.months} ay</strong> eklenecek.
                        </div>
                      )}

                      <button
                        className="btn w-100 py-2 mt-3"
                        style={{ borderRadius: 999, fontSize: "1rem", fontWeight: 700, backgroundColor: planColor.accent, borderColor: planColor.accent, color: "#fff", transition: "filter .2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
                        onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
                        disabled={submitting || !priceCalculation}
                        onClick={formik.handleSubmit}
                      >
                        {submitting ? (
                          <><Spinner size="sm" className="me-2" /> 3D Secure Yönlendiriliyor...</>
                        ) : (
                          <><i className="bx bx-lock-alt me-2" />Ödemeyi Tamamla</>
                        )}
                      </button>

                      <div className="text-center mt-3">
                        <small className="text-muted">
                          <i className="bx bx-shield-check me-1" />
                          256-bit SSL ile güvenli ödeme
                        </small>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default Checkout
